import React from 'react';
import ArrowRightIcon from '../ArrowRightIcon';

/**
 * IndentedRow Component
 *
 * Renders a table row with optional indentation and an arrow icon for substeps.
 *
 * ### Props:
 * - **data**:
 *   `{ columns: Array<{ id: string | number, value: string | JSX.Element, isIndentationColumn?: boolean }> }`
 *   An array of column objects where:
 *   - `id`: Unique identifier for the column.
 *   - `value`: Content to display inside the column.
 *   - `isIndentationColumn` (optional):
 *     Specifies whether the indentation should be displayed.
 *
 * - **isSubstep** (`boolean`, default: `true`):
 *   Determines whether the row represents a substep and whether the arrow should be rendered.
 *
 * - **isLastRow** (`boolean`):
 *   Indicates if this is the last row, which cuts the vertical line halfway.
 *
 * ### Behavior:
 * - `isIndentationColumn` **determines the position** of the indentation.
 * - `isSubstep` **controls the visibility** of the arrow.
 */
const IndentedRow = ({ data, isLastRow, isSubstep = true }) => {
  const { columns } = data;

  const isLastRowClass = isLastRow ? 'bottom-1/2' : 'bottom-[-20px]';

  const verticalLineClasses = `
    absolute 
    w-[1.7px] 
    bg-[#D3D6DC] 
    top-[-20px] 
    ${isLastRowClass} 
    left-[52.5px]
  `;

  return (
    <tr
      data-testid="indented-row"
      className="relative bg-[#F8F8F8] border-b-2 border-[#DDDFE2] whitespace-nowrap"
    >
      {columns.map((column, index) => {
        return (
          <td
            key={column.id}
            className={`py-4 px-4 text-center text-black ${index === 0 ? 'pl-[50px]' : ''}`}
          >
            {column.isIndentationColumn ? (
              <div className="flex items-center justify-center">
                {isSubstep && (
                  <div className="relative flex items-center pl-12">
                    <ArrowRightIcon className="w-6 h-6" />
                    <span className={verticalLineClasses} />
                  </div>
                )}
                <span className="pl-1">{column.value}</span>
              </div>
            ) : (
              <span>{column.value}</span>
            )}
          </td>
        );
      })}
    </tr>
  );
};

export default IndentedRow;
